import React, { useEffect, useState, useRef } from "react";
import styled from "styled-components";
import { Link, useLocation } from "react-router-dom";
// import constants
import CONSTANTS from "../utils/constants/constants";
// import assets
import headerLogo from "../assets/sr-logo-header.png";
// import common components
import MenuIcon from "../common-components/MenuIcon";
import Typography, { TYPE_ELEMENTS } from "../common-components/Typography";

const { HOME, ABOUT_US, APPLICATIONS, TECHNOLOGY, BLOG, CONTACT_US } =
  CONSTANTS.LINKS;

const NavbarContainer = styled.nav`
  position: sticky;
  top: 0;
  display: flex;
  justify-content: space-between;
  align-items: center;
  width: 100vw;
  max-width: 100%;
  height: auto;
  padding: 28px 48px;
  margin-top: 0;
  margin-bottom: 0;
  box-sizing: border-box;
  background-color: ${(props) => props.bgColor};
  backdrop-filter: ${(props) => props.bgColor !== "transparent" ? "blur(10px)" : "none"};
  transition: background-color 0.3s ease;
  z-index: 1000;
  color: white;

  @media (max-width: 1280px) {
    padding: 28px 40px;
  }
  @media (max-width: 1024px) {
    padding: 20px 32px;
  }
  @media (max-width: 768px) {
    padding: 16px 24px;
  }
  @media (max-width: 480px) {
    padding: 12px 16px;
  }
`;

const Logo = styled.img`
  width: 100px;
  height: auto;
  cursor: pointer;

  @media (max-width: 1280px) {
    width: 90px;
  }
  @media (max-width: 1024px) {
    width: 80px;
  }
  @media (max-width: 768px) {
    width: 70px;
  }
`;

const NavLinks = styled.div`
  position: relative;
  display: flex;
  // gap: 40px;
  box-sizing: border-box;

  @media (max-width: 1024px) {
    display: ${(props) => (props.isMenuOpen ? "flex" : "none")};
    flex-direction: column;
    gap: 0px;
    position: absolute;
    top: 100%;
    left: 52vw;
    width: 48vw;
    height: 100dvh;
    // overflow: scroll;
    background-color: black;
  }

  @media (max-width: 480px) {
    width: 64vw;
    left: 36vw;
  }

  @media (max-width: 768px) and (orientation: landscape) and (hover: none) and (pointer: coarse) {
    width: 56vw;
    left: 44vw;
  }
`;

const NavLinksWrapper = styled.div`
  display: flex;
  gap: 40px;
  box-sizing: border-box;

  @media (max-width: 1024px) {
    flex-direction: column;
    gap: 0px;
    height: 70%;
    overflow: scroll;
  }

  scrollbar-width: none; /* Firefox */
  -ms-overflow-style: none; /* Internet Explorer 10+ */

  /* Hide scrollbar for WebKit (Chrome, Safari, Opera) */
  &::-webkit-scrollbar {
    display: none;
  }
`;

const StyledLink = styled(Link)`
  position: relative;
  height: fit-content;
  width: fit-content;
  padding: 6px;
  color: ${(props) => props.activeColor};
  text-decoration: none;

  position: relative;
  text-decoration: none;
  transition: color 0.3s ease;

  &::after {
    content: "";
    position: absolute;
    left: 0;
    width: 100%;
    height: 0.5px;
    background-color: white;
    transform: scaleX(0);
    transition: transform 0.3s ease;
  }

  &:hover::after {
    transform: scaleX(1);
  }

  &:hover {
    color: ${(props) => props.activeColor};
  }

  @media (max-width: 1024px) {
    height: fit-content;
    width: 100%;
    padding: 4px 16px;
    display: flex;
    flex-direction: column;
    align-items: start;
    justify-content: flex-start;
    border-bottom: 1px solid white;
  }
`;

const DropdownMenu = styled.div`
  position: absolute;
  top: 100%;
  left: 0;
  background-color: black;
  padding: 8px 0;
  width: fit-content;
  min-width: 360px;
  border: 1px solid white;
  display: ${(props) => (props.isOpen ? "block" : "none")};
  box-sizing: border-box;

  @media (max-width: 1024px) {
    position: relative;
    display: ${(props) => (props.isOpen ? "flex" : "none")};
    flex-direction: column;
    align-items: flex-start;
    justify-content: flex-start;
    top: 0%;
    width: 100%;
    border: none;
  }
`;

const DropdownItem = styled(Link)`
  display: block;
  padding: 8px 16px;
  color: white;
  text-decoration: none;
  width: 100%;
  box-sizing: border-box;
  color: ${(props) => props.activeColor};

  &:hover {
    color: #FF4F00;
    transition: color 0.3s ease;
  }

  &::after {
    content: none;
    display: none;
  }

  @media (max-width: 1024px) {
    padding: 8px 0px;
  }
`;

const SpacerDiv = styled.div`
  width: 100px;
  height: 48px;

  @media (max-width: 1400px) {
    height: 40px;
  }
  @media (max-width: 1280px) {
    width: 80px;
  }
  @media (max-width: 1024px) {
    display: none;
  }
`;

const Navbar = () => {
  const [isScrolled, setIsScrolled] = useState(false);
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [isTechMenuOpen, setIsTechMenuOpen] = useState(false);
  const location = useLocation();
  const navRef = useRef(null);

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 0);
    };

    window.addEventListener("scroll", handleScroll);
    return () => {
      window.removeEventListener("scroll", handleScroll);
    };
  }, []);

  // Close menu when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (navRef.current && !navRef.current.contains(event.target)) {
        setIsMenuOpen(false);
        setIsTechMenuOpen(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, []);

  return (
    <NavbarContainer
      ref={navRef}
      bgColor={isScrolled || isMenuOpen ? "#00000069" : "transparent"}
    >
      <Logo src={headerLogo} alt="Logo" onClick={() => window.location.href = '/'} />
      <MenuIcon isOpen={isMenuOpen} onMenuOpen={setIsMenuOpen} />
      <NavLinks className="nav-links"
        onScroll={(e) => e.stopPropagation()}
        onWheel={(e) => e.stopPropagation()}
        isMenuOpen={isMenuOpen}
      >
        <NavLinksWrapper>
          {[HOME, ABOUT_US, APPLICATIONS, TECHNOLOGY, BLOG, CONTACT_US].map(
            (item, i) => (
              <>
                {item.LABEL !== TECHNOLOGY.LABEL ? (
                  <StyledLink
                    key={i}
                    to={item.LINK}
                    activeColor={
                      location.pathname === item.LINK ? "#FF4F00" : "inherit"
                    }
                    onClick={() => {
                      setIsMenuOpen(false);
                      setIsTechMenuOpen(false);
                    }}
                  >
                    <Typography typeElement={window.innerWidth > 480 ? TYPE_ELEMENTS.SUBTITLE_1 : TYPE_ELEMENTS.SUBTITLE_2}>
                      {item.LABEL}
                    </Typography>
                  </StyledLink>
                ) : (
                  <StyledLink
                    onMouseEnter={() => {
                      if (window.innerWidth > 1024) {
                        setIsTechMenuOpen(true);
                      }
                    }}
                    onMouseLeave={() => {
                      if (window.innerWidth > 1024) {
                        setIsTechMenuOpen(false);
                      }
                    }}
                    onClick={() => {
                      if (window.innerWidth <= 1024) {
                        setIsTechMenuOpen((prev) => !prev);
                      }
                    }}
                    activeColor={
                      location.pathname.startsWith(TECHNOLOGY.LINK)
                        ? "#FF4F00"
                        : "inherit"
                    }
                  >
                    <Typography typeElement={window.innerWidth > 480 ? TYPE_ELEMENTS.SUBTITLE_1 : TYPE_ELEMENTS.SUBTITLE_2}>
                      {TECHNOLOGY.LABEL}
                    </Typography>
                    <DropdownMenu isOpen={isTechMenuOpen}>
                      {TECHNOLOGY.PAGES.map((techPage, i) => (
                        <DropdownItem
                          key={i}
                          to={techPage.LINK}
                          onClick={() => {
                            setIsMenuOpen(false);
                            setIsTechMenuOpen(false);
                          }}
                          activeColor={
                            location.pathname.includes(techPage.LINK) ? "#FF4F00" : "#fff"
                          }
                        >
                          <Typography typeElement={window.innerWidth > 480 ? TYPE_ELEMENTS.SUBTITLE_1 : TYPE_ELEMENTS.SUBTITLE_2}>
                            {techPage.LABEL}
                          </Typography>
                        </DropdownItem>
                      ))}
                    </DropdownMenu>
                  </StyledLink>
                )}
              </>
            )
          )}
        </NavLinksWrapper>
      </NavLinks>
      <SpacerDiv />
    </NavbarContainer>
  );
};

export default Navbar;
